import { useMemo } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  jobChainTemplates,
  normalizeJobChainTemplateListParams,
} from '@/api/JobChainTemplates/JobChainTemplates';
import {
  isJobChainTemplateApiFailure,
  type JobChainTemplateApiResponse,
  type JobChainTemplateApiFailure,
  type JobChainTemplateDetail,
  type JobChainTemplateListParams,
  type JobChainTemplatePage,
  type JobChainTemplateMetadata,
} from '@/api/JobChainTemplates/types';

export const jobChainTemplateListQueryKey = (
  params: JobChainTemplateListParams,
) => {
  const normalized = normalizeJobChainTemplateListParams(params);
  return [QUERY_KEYS.jobChainTemplateList, JSON.stringify(normalized)] as const;
};

export const useJobChainTemplateList = (params: JobChainTemplateListParams) => {
  const normalized = useMemo(
    () => normalizeJobChainTemplateListParams(params),
    [params],
  );

  return useQuery<JobChainTemplatePage, JobChainTemplateApiFailure>({
    queryKey: jobChainTemplateListQueryKey(normalized),
    queryFn: async () => {
      const response = await jobChainTemplates.listTemplates(normalized);
      if (isJobChainTemplateApiFailure(response)) throw response;
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useJobChainTemplateMetadata = () =>
  useQuery<JobChainTemplateMetadata, JobChainTemplateApiFailure>({
    queryKey: [QUERY_KEYS.jobChainTemplateMetadata] as const,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await jobChainTemplates.getMetadata();
      if (isJobChainTemplateApiFailure(response)) throw response;
      return response.data;
    },
  });

export const unwrapTemplateResponse = <T>(
  response: JobChainTemplateApiResponse<T>,
): T => {
  if (isJobChainTemplateApiFailure(response)) throw response;
  return response.data;
};

export const templateDetailKey = (id: number) =>
  [QUERY_KEYS.jobChainTemplateDetail, id] as const;
export const useJobChainTemplateDetail = (id: number) =>
  useQuery({
    queryKey: templateDetailKey(id),
    staleTime: 30_000,
    enabled: Number.isInteger(id) && id > 0,
    queryFn: async () =>
      unwrapTemplateResponse(await jobChainTemplates.getDetail(id)),
  });

export const useCacheTemplateDetail = () => {
  const client = useQueryClient();
  return async (detail: JobChainTemplateDetail) => {
    const queryKey = templateDetailKey(detail.id);
    // Late reads must not replace the command response or revive stale template history.
    await Promise.all([
      client.cancelQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateList],
      }),
      client.cancelQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateDetail],
      }),
    ]);
    await Promise.all([
      client.invalidateQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateList],
        refetchType: 'none',
      }),
      client.invalidateQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateDetail],
        refetchType: 'none',
      }),
    ]);
    // Seed last so this detail stays fresh for the immediate route transition.
    // Creating a template or Draft changes neither metadata nor permissions.
    client.setQueryData(queryKey, detail);
  };
};

export const useInvalidateTemplates = () => {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateList],
      }),
      client.invalidateQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateDetail],
      }),
      client.invalidateQueries({
        queryKey: [QUERY_KEYS.jobChainTemplateMetadata],
      }),
    ]);
  };
};
